import { useState, useEffect } from 'react';
import { formatDateTime, formatDuration, getStatusLabel } from '@/utils';
import { useFieldVisibility, FIELD_DEFINITIONS } from '@/context/FieldVisibilityContext';
import { useTable } from '@/context/TableContext';
import type { CallRecord } from '@/types';
import { URGENCY_CONFIG } from '@/types';
import { X, User, Clock, FileText, Mic, AlertTriangle, CheckCircle, Calendar, RotateCcw, Database } from 'lucide-react';
import { updateCallRecordStatus } from '@/services/supabase';

interface RecordDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: Record<string, unknown> | null;
  columns?: string[];
  title?: string;
  onStatusChange?: (id: string, newStatus: boolean) => void;
}

export function RecordDetailsModal({ 
  isOpen, 
  onClose, 
  record, 
  columns = [], 
  title = 'Record Details', 
  onStatusChange 
}: RecordDetailsModalProps) {
  const { isFieldVisible, getVisibleFields } = useFieldVisibility();
  const { currentTable } = useTable();
  const [statusLoading, setStatusLoading] = useState(false);
  const [localRecord, setLocalRecord] = useState<Record<string, unknown> | null>(null);

  // Sync local record when prop changes
  useEffect(() => {
    if (record) {
      setLocalRecord(record);
    }
  }, [record]);

  if (!isOpen || !localRecord) return null;

  // Check if this is a alsalama_call_records table (for status toggle) - must be declared before use
  const isCallRecordsTable = currentTable === 'alsalama_call_records';

  // Get visible fields for modal context
  const getVisibleFieldsForModal = () => {
    const visibleModalFields = getVisibleFields('modal');
    
    if (isCallRecordsTable) {
      // For alsalama_call_records, use the predefined field definitions filtered by visibility
      return FIELD_DEFINITIONS.filter(f => isFieldVisible(f.key, 'modal')).map(f => f.key);
    } else {
      // For other tables, use dynamic columns filtered by visibility settings
      if (columns.length > 0) {
        // If visibility config exists for this table, filter by it
        if (visibleModalFields.length > 0) {
          return columns.filter(col => col !== 'id' && col !== 'user_id' && visibleModalFields.includes(col));
        }
        // Otherwise, show all columns
        return columns.filter(col => col !== 'id' && col !== 'user_id');
      }
      return Object.keys(localRecord).filter(key => key !== 'id' && key !== 'user_id');
    }
  };

  // Determine which columns to display
  const displayColumns = getVisibleFieldsForModal();

  // Get visible fields for modal context (only for alsalama_call_records)
  const visibleFields = isCallRecordsTable 
    ? FIELD_DEFINITIONS.filter(f => isFieldVisible(f.key, 'modal'))
    : [];

  // Group fields by category for alsalama_call_records
  const groupedFields = isCallRecordsTable 
    ? visibleFields.reduce((acc, field) => {
        const category = field.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(field);
        return acc;
      }, {} as Record<string, typeof FIELD_DEFINITIONS>)
    : {};

  // Helper to get status styles (only for alsalama_call_records)
  type StatusInfo = { label: string; color: string; bgColor: string };
  const statusInfo: StatusInfo | null = isCallRecordsTable && typeof localRecord.status === 'boolean'
    ? getStatusLabel(localRecord.status)
    : null;

  // Helper to format field value based on content type
  const formatFieldValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return '-';
    
    // Handle specific field types for alsalama_call_records
    if (isCallRecordsTable) {
      if (key === 'created_at') {
        return formatDateTime(value as string, 'en');
      }
      if (key === 'call_duration_sec') {
        return formatDuration(value as number | null);
      }
      if (key === 'status') {
        return getStatusLabel(value as boolean | null).label;
      }
      if (key === 'urgency') {
        const urgencyConfig = URGENCY_CONFIG[value as keyof typeof URGENCY_CONFIG];
        return urgencyConfig?.label || String(value);
      }
    }
    
    // Default formatting
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  // Helper to determine if a field should be displayed as a long text area
  const isLongTextField = (key: string): boolean => {
    const longTextKeys = ['call_summary', 'inquiry', 'notes', 'description', 'content', 'message', 'details'];
    return longTextKeys.some(longKey => key.toLowerCase().includes(longKey));
  };

  // Handle status toggle (only for alsalama_call_records)
  const handleStatusToggle = async () => {
    if (statusLoading || !onStatusChange || !isCallRecordsTable) return;
    
    setStatusLoading(true);
    const newStatus = !localRecord.status;
    
    try {
      const result = await updateCallRecordStatus(localRecord.id as string, newStatus);
      if (result.error) {
        console.error('Failed to update status:', result.error);
      } else {
        // Update local state immediately
        setLocalRecord(prev => prev ? { ...prev, status: newStatus } : null);
        // Notify parent
        onStatusChange(localRecord.id as string, newStatus);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed !inset-0 !z-[100] bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Content - redesigned modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[95vw] md:max-w-[900px] lg:max-w-[1000px] max-h-[90vh] md:max-h-[85vh] bg-background rounded-xl shadow-2xl overflow-hidden !z-[110] flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background px-4 md:px-6 py-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-foreground">{title}</h3>
              <p className="text-xs text-muted-foreground font-mono">{localRecord.id as string || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Status Badge - only show for alsalama_call_records */}
            {isCallRecordsTable && statusInfo ? (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            ) : null}
            {/* Urgency Badge - only show for alsalama_call_records */}
            {isCallRecordsTable && localRecord.urgency ? (
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.bg || URGENCY_CONFIG.low.bg,
                  color: URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.color || URGENCY_CONFIG.low.color,
                }}
              >
                {URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.label || 'Low'} Urgency
              </span>
            ) : null}
            <button onClick={onClose} className="p-2 hover:bg-accent rounded-lg transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - scrollable */}
        <div className="overflow-y-auto scroll-smooth scrollbar-elegant p-4 md:p-6">
          {isCallRecordsTable ? (
            // Original alsalama_call_records layout
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Left Column - Basic Info */}
              <div className="col-span-1 md:col-span-2 lg:col-span-2 flex flex-col gap-4 md:gap-6">
                {/* Basic Information Section */}
                <div className="bg-muted/30 rounded-xl p-4 md:p-5 border">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <User className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm uppercase tracking-wider">Caller Information</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {groupedFields.basic?.map((field) => (
                      <div key={field.key as string} className="">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{field.label}</label>
                        <p className="text-sm font-medium">{localRecord[field.key as keyof CallRecord] as string || '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Content Section */}
                <div className="bg-muted/30 rounded-xl p-4 md:p-5 border">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <FileText className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm uppercase tracking-wider">Call Content</h4>
                  </div>
                  <div className="flex flex-col gap-3 md:gap-4">
                    {groupedFields.details?.map((field) => {
                      const value = localRecord[field.key as keyof CallRecord] as string;
                      return (
                        <div key={field.key as string} className="">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{field.label}</label>
                          <div className="p-4 bg-background rounded-lg border min-h-[80px]">
                            <p className="text-sm leading-relaxed">{value || '-'}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column - System Info & Audio */}
              <div className="flex flex-col gap-4 md:gap-6">
                {/* Quick Stats */}
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 md:p-5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <AlertTriangle className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm uppercase tracking-wider">Metrics & Stats</h4>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                      <span className="text-xs text-muted-foreground">Call Duration</span>
                      <span className="text-sm font-semibold flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDuration(localRecord.call_duration_sec as number | null)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                      <span className="text-xs text-muted-foreground">Resolution</span>
                      <span className={`text-sm font-semibold flex items-center gap-1 ${
                        localRecord.status ? 'text-emerald-600' : 'text-amber-600'
                      }`}>
                        {localRecord.status ? (
                          <>
                            <CheckCircle className="h-3 w-3" /> Resolved
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3" /> Pending
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                      <span className="text-xs text-muted-foreground">Urgency Level</span>
                      <span
                        className="text-sm font-semibold px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.bg || URGENCY_CONFIG.low.bg,
                          color: URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.color || URGENCY_CONFIG.low.color,
                        }}
                      >
                        {URGENCY_CONFIG[localRecord.urgency as keyof typeof URGENCY_CONFIG]?.label || 'Low'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Audio Recording */}
                <div className="bg-muted/30 rounded-xl p-4 md:p-5 border">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <Mic className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm uppercase tracking-wider">Recording</h4>
                  </div>
                  {localRecord.audio_uri ? (
                    <button
                      onClick={() => window.open(localRecord.audio_uri as string, '_blank', 'noopener,noreferrer')}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      <span className="font-medium">Play Recording</span>
                    </button>
                  ) : (
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">No recording available</p>
                    </div>
                  )}
                </div>

                {/* System Details */}
                <div className="bg-muted/30 rounded-xl p-4 md:p-5 border">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <Calendar className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm uppercase tracking-wider">System Details</h4>
                  </div>
                  <div className="flex flex-col gap-3">
                    {groupedFields.system?.map((field) => (
                      <div key={field.key as string} className="">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{field.label}</label>
                        <p className="text-xs font-mono p-2 bg-background rounded break-all">{localRecord[field.key as keyof CallRecord] as string || '-'}</p>
                      </div>
                    ))}
                    {groupedFields.system?.some(f => f.key === 'created_at') && (
                      <div className="">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created At</label>
                        <p className="text-xs p-2 bg-background rounded flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {formatDateTime(localRecord.created_at as string, 'en')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Dynamic layout for other tables
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {displayColumns.map((key) => (
                <div key={key} className="bg-muted/30 rounded-xl p-4 border">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                  {isLongTextField(key) ? (
                    <div className="p-3 bg-background rounded-lg border min-h-[80px]">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {formatFieldValue(key, localRecord[key])}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm font-medium">
                      {formatFieldValue(key, localRecord[key])}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-muted/30 px-4 md:px-6 py-4 border-t shrink-0 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            Record ID: <span className="font-mono">{localRecord.id as string || '-'}</span>
          </p>
          <div className="flex items-center gap-3">
            {/* Status Toggle Button - only for alsalama_call_records */}
            {onStatusChange && isCallRecordsTable && (
              <button
                onClick={handleStatusToggle}
                disabled={statusLoading}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  localRecord.status 
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50' 
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50'
                } ${statusLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={localRecord.status ? 'Mark as Pending' : 'Mark as Resolved'}
              >
                {statusLoading ? (
                  <RotateCcw className="h-4 w-4 animate-spin" />
                ) : localRecord.status ? (
                  <Clock className="h-4 w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                <span>{localRecord.status ? 'Mark Pending' : 'Mark Resolved'}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
